import React, { useState } from 'react';
import './LogOfferWizard.css';

const STEPS = {
  LISTING: 1,
  BUYER: 2,
  OFFER: 3,
  REVIEW: 4
};

const STEP_LABELS = {
  1: 'Listing',
  2: 'Buyer',
  3: 'Offer',
  4: 'Review'
};

function LogOfferWizard({ onClose, onSubmit, listings = [] }) {
  const [currentStep, setCurrentStep] = useState(STEPS.LISTING);
  const [submitting, setSubmitting] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [financingDropdownOpen, setFinancingDropdownOpen] = useState(false);
  const [formData, setFormData] = useState({
    listingId: '',
    buyerName: '',
    buyerEmail: '',
    buyerPhone: '',
    buyerAgent: '',
    buyerAgentEmail: '',
    buyerAgentPhone: '',
    offerAmount: '',
    earnestMoney: '',
    financingType: 'conventional',
    closingDate: '',
    expirationDate: '',
    contingencies: '',
    notes: ''
  });

  const financingOptions = [
    { value: 'cash', label: 'Cash' },
    { value: 'conventional', label: 'Conventional' },
    { value: 'fha', label: 'FHA' },
    { value: 'va', label: 'VA' },
    { value: 'usda', label: 'USDA' }
  ];

  const activeListings = listings.filter(l => l.status === 'active');
  const totalSteps = Object.keys(STEPS).length;
  const progressPercent = (currentStep / totalSteps) * 100;

  const formatDateInput = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 2) {
      return digits;
    } else if (digits.length <= 4) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    } else {
      return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'offerAmount' || name === 'earnestMoney') {
      const numericValue = value.replace(/[^0-9]/g, '');
      setFormData(prev => ({ ...prev, [name]: numericValue }));
    } else if (name === 'closingDate' || name === 'expirationDate') {
      const formattedDate = formatDateInput(value);
      setFormData(prev => ({ ...prev, [name]: formattedDate }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case STEPS.LISTING:
        return formData.listingId !== '';
      case STEPS.BUYER:
        return formData.buyerName.trim() !== '' && formData.buyerAgent.trim() !== '';
      case STEPS.OFFER:
        return formData.offerAmount !== '' && formData.closingDate.length === 10;
      case STEPS.REVIEW:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps && canProceed()) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error submitting offer:', error);
      alert('Failed to save offer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getSelectedListing = () => {
    return listings.find(l => l.id === parseInt(formData.listingId));
  };

  const renderStepLabel = (stepNum, label) => {
    let className = 'low-breadcrumb-item';
    if (stepNum === currentStep) className += ' active';
    else if (stepNum < currentStep) className += ' completed';
    
    const prefix = stepNum < currentStep ? '✓ ' : '';
    return <span key={stepNum} className={className}>{prefix}{label}</span>;
  };

  const renderListingStep = () => {
    const selectedListing = listings.find(l => l.id === parseInt(formData.listingId));
    
    if (activeListings.length === 0) {
      return (
        <div className="low-step">
          <div className="low-empty-state">
            <h3>No active listings yet</h3>
            <p>Publish a listing to start logging offers.</p>
            <button className="low-btn-create" onClick={onClose}>
              Create Listing
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="low-step">
        <h2 className="low-step-title">Select a Listing</h2>
        <p className="low-step-subtitle">Choose which property this offer is for.</p>
        
        <div className="low-form">
          <div className="low-field">
            <label>Listing <span className="low-required">*</span></label>
            <div className="low-custom-select">
              <div 
                className={`low-select-trigger ${dropdownOpen ? 'open' : ''}`}
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <span className={selectedListing ? '' : 'placeholder'}>
                  {selectedListing 
                    ? `${selectedListing.address}, ${selectedListing.city} - $${selectedListing.price?.toLocaleString()}`
                    : 'Choose a listing...'}
                </span>
                <svg className="low-select-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
              {dropdownOpen && (
                <div className="low-select-options">
                  {activeListings.map(listing => (
                    <div 
                      key={listing.id} 
                      className={`low-select-option ${formData.listingId === String(listing.id) ? 'selected' : ''}`}
                      onClick={() => {
                        setFormData(prev => ({ ...prev, listingId: String(listing.id) }));
                        setDropdownOpen(false);
                      }}
                    >
                      {listing.address}, {listing.city} - ${listing.price?.toLocaleString()}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBuyerStep = () => {
    return (
      <div className="low-step">
        <h2 className="low-step-title">Buyer Information</h2>
        <p className="low-step-subtitle">Enter the buyer and agent contact details.</p>
        
        <div className="low-form">
          <div className="low-row">
            <div className="low-field">
              <label>Buyer Name <span className="low-required">*</span></label>
              <input
                type="text"
                name="buyerName"
                value={formData.buyerName}
                onChange={handleChange}
                placeholder="John Smith"
              />
            </div>
            <div className="low-field">
              <label>Buyer Email</label>
              <input
                type="email"
                name="buyerEmail"
                value={formData.buyerEmail}
                onChange={handleChange}
                placeholder="buyer@email.com"
              />
            </div>
          </div>
          <div className="low-row">
            <div className="low-field">
              <label>Buyer Phone</label>
              <input
                type="tel"
                name="buyerPhone"
                value={formData.buyerPhone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
              />
            </div>
            <div className="low-field"></div>
          </div>
          
          <div style={{ borderTop: '1px solid #e5e7eb', margin: '8px 0 16px 0' }}></div>
          
          <div className="low-row">
            <div className="low-field">
              <label>Buyer's Agent <span className="low-required">*</span></label>
              <input
                type="text"
                name="buyerAgent"
                value={formData.buyerAgent}
                onChange={handleChange}
                placeholder="Agent name"
              />
            </div>
            <div className="low-field">
              <label>Agent Email</label>
              <input
                type="email"
                name="buyerAgentEmail"
                value={formData.buyerAgentEmail}
                onChange={handleChange}
                placeholder="agent@email.com"
              />
            </div>
          </div>
          <div className="low-row">
            <div className="low-field">
              <label>Agent Phone</label>
              <input
                type="tel"
                name="buyerAgentPhone"
                value={formData.buyerAgentPhone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
              />
            </div>
            <div className="low-field"></div>
          </div>
        </div>
      </div>
    );
  };

  const renderOfferStep = () => {
    return (
      <div className="low-step">
        <h2 className="low-step-title">Offer Details</h2>
        <p className="low-step-subtitle">Enter the terms of the offer.</p>
        
        <div className="low-form">
          <div className="low-row">
            <div className="low-field">
              <label>Offer Amount <span className="low-required">*</span></label>
              <input
                type="text"
                name="offerAmount"
                value={formData.offerAmount ? `$${Number(formData.offerAmount).toLocaleString()}` : ''}
                onChange={handleChange}
                placeholder="$0"
                autoComplete="off"
              />
            </div>
            <div className="low-field">
              <label>Earnest Money</label>
              <input
                type="text"
                name="earnestMoney"
                value={formData.earnestMoney ? `$${Number(formData.earnestMoney).toLocaleString()}` : ''}
                onChange={handleChange}
                placeholder="$0"
                autoComplete="off"
              />
              <span style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Typically 2-3% of the purchase price</span>
            </div>
          </div>
          <div className="low-row">
            <div className="low-field">
              <label>Financing Type</label>
              <div className="low-custom-select">
                <div 
                  className={`low-select-trigger ${financingDropdownOpen ? 'open' : ''}`}
                  onClick={() => setFinancingDropdownOpen(!financingDropdownOpen)}
                >
                  <span>{financingOptions.find(o => o.value === formData.financingType)?.label}</span>
                  <svg className="low-select-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
                {financingDropdownOpen && (
                  <div className="low-select-options">
                    {financingOptions.map(option => (
                      <div 
                        key={option.value} 
                        className={`low-select-option ${formData.financingType === option.value ? 'selected' : ''}`}
                        onClick={() => {
                          setFormData(prev => ({ ...prev, financingType: option.value }));
                          setFinancingDropdownOpen(false);
                        }}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="low-field">
              <label>Proposed Closing Date <span className="low-required">*</span></label>
              <input
                type="text"
                name="closingDate"
                value={formData.closingDate}
                onChange={handleChange}
                placeholder="MM/DD/YYYY"
                maxLength="10"
              />
            </div>
          </div>
          <div className="low-row">
            <div className="low-field">
              <label>Offer Expiration Date</label>
              <input
                type="text"
                name="expirationDate"
                value={formData.expirationDate}
                onChange={handleChange}
                placeholder="MM/DD/YYYY"
                maxLength="10"
              />
            </div>
            <div className="low-field"></div>
          </div>
          <div className="low-field">
            <label>Contingencies</label>
            <textarea
              name="contingencies"
              value={formData.contingencies}
              onChange={handleChange}
              placeholder="Inspection, financing, appraisal..."
              rows={2}
            />
          </div>
          <div className="low-field">
            <label>Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Any additional notes about this offer..."
              rows={3}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderReviewStep = () => {
    const selectedListing = getSelectedListing();
    
    return (
      <div className="low-step">
        <h2 className="low-step-title">Review Offer</h2>
        <p className="low-step-subtitle">Confirm the offer details before saving.</p>
        
        <div className="low-review-card">
          <div className="low-review-header">
            <div className="low-review-property">
              {selectedListing?.address}, {selectedListing?.city}, {selectedListing?.state}
            </div>
            <div className="low-review-list-price">
              List Price: ${selectedListing?.price?.toLocaleString()}
            </div>
          </div>

          <div className="low-review-grid">
            {/* Row 1: Offer Price & Earnest Money */}
            <div className="low-review-cell">
              <div className="low-review-label">Offer Price</div>
              <div className="low-review-value">${Number(formData.offerAmount).toLocaleString()}</div>
            </div>
            <div className="low-review-cell">
              <div className="low-review-label">Earnest Money</div>
              <div className="low-review-value">{formData.earnestMoney ? `$${Number(formData.earnestMoney).toLocaleString()}` : '—'}</div>
            </div>
            
            {/* Row 2: Financing & Closing Date */}
            <div className="low-review-cell">
              <div className="low-review-label">Financing</div>
              <div className="low-review-value" style={{ textTransform: 'capitalize' }}>{formData.financingType}</div>
            </div>
            <div className="low-review-cell">
              <div className="low-review-label">Closing Date</div>
              <div className="low-review-value">{formData.closingDate || '—'}</div>
            </div>
            
            {/* Row 3: Expiration Date */}
            <div className="low-review-cell">
              <div className="low-review-label">Expiration Date</div>
              <div className="low-review-value">{formData.expirationDate || '—'}</div>
            </div>
            <div className="low-review-cell empty"></div>
            
            {/* Row 4: Buyer & Buyer's Agent */}
            <div className="low-review-cell">
              <div className="low-review-label">Buyer</div>
              <div className="low-review-value">{formData.buyerName}</div>
            </div>
            <div className="low-review-cell">
              <div className="low-review-label">Buyer's Agent</div>
              <div className="low-review-value">{formData.buyerAgent || '—'}</div>
            </div>
          </div>

          {(formData.contingencies || formData.notes) && (
            <div className="low-review-notes">
              {formData.contingencies && (
                <>
                  <div className="low-review-notes-label">Contingencies</div>
                  <p className="low-review-notes-text">{formData.contingencies}</p>
                </>
              )}
              {formData.notes && (
                <>
                  <div className="low-review-notes-label" style={{ marginTop: formData.contingencies ? '12px' : '0' }}>Notes</div>
                  <p className="low-review-notes-text">{formData.notes}</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderFooter = () => {
    if (activeListings.length === 0 && currentStep === STEPS.LISTING) {
      return null;
    }

    return (
      <div className="low-footer">
        {currentStep > 1 && (
          <button type="button" className="low-btn-back" onClick={handleBack}>
            Back
          </button>
        )}
        <div className="low-footer-spacer"></div>
        {currentStep < totalSteps ? (
          <button
            type="button"
            className="low-btn-primary"
            onClick={handleNext}
            disabled={!canProceed()}
          >
            Continue
          </button>
        ) : (
          <button
            type="button"
            className="low-btn-primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Save Offer'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="low-overlay" onClick={onClose}>
      <div className="low-modal" onClick={e => e.stopPropagation()}>
        {/* Progress Bar */}
        <div className="low-progress">
          <div className="low-progress-bar" style={{ width: `${progressPercent}%` }} />
        </div>
        
        {/* Header */}
        <div className="low-header">
          <div className="low-breadcrumb">
            {Object.entries(STEP_LABELS).map(([stepNum, label], index) => (
              <React.Fragment key={stepNum}>
                {index > 0 && <span className="low-breadcrumb-sep">›</span>}
                {renderStepLabel(parseInt(stepNum), label)}
              </React.Fragment>
            ))}
          </div>
          <button className="low-close" onClick={onClose}>×</button>
        </div>
        
        {/* Content */}
        <div className="low-content">
          {currentStep === STEPS.LISTING && renderListingStep()}
          {currentStep === STEPS.BUYER && renderBuyerStep()}
          {currentStep === STEPS.OFFER && renderOfferStep()}
          {currentStep === STEPS.REVIEW && renderReviewStep()}
        </div>
        
        {/* Footer */}
        {renderFooter()}
      </div>
    </div>
  );
}

export default LogOfferWizard;
